import React, { useCallback, useState } from 'react';
import { Loader2 } from 'lucide-react';

import {
  DraggableDialog,
  DraggableDialogContent,
  DraggableDialogHeader,
  DraggableDialogTitle,
  DraggableDialogDescription,
  DraggableDialogFooter,
} from '@/components/ui/draggable-dialog';
import { Button } from '@/components/ui/button';
import { EntitySettingsManagement } from '@/components/entities/EntitySettingsManagement';
import { useEntitySettings } from '@/features/dashboard/hooks/useEntitySettings';
import { useEntitySettingsModalStore } from '@/store/entitySettingsModal.store';

export function EntitySettingsGlobalModal() {
  const { isOpen, entityId, close } = useEntitySettingsModalStore();
  const entitySettings = useEntitySettings();

  const [entitySaveHandler, setEntitySaveHandler] = useState<(() => Promise<void>) | null>(null);
  const [canSaveEntity, setCanSaveEntity] = useState(false);
  const [isSavingEntity, setIsSavingEntity] = useState(false);

  const handleEntitySaveAction = useCallback((saveHandler: () => Promise<void>, canSave: boolean, isSaving: boolean) => {
    setEntitySaveHandler(() => saveHandler);
    setCanSaveEntity(canSave);
    setIsSavingEntity(isSaving);
  }, []);

  if (!entityId) return null;

  return (
    <DraggableDialog open={isOpen} onOpenChange={(open) => { if (!open) close(); }}>
      <DraggableDialogContent className="flex flex-col p-0 gap-0">
        <DraggableDialogHeader className="p-6 border-b flex-shrink-0">
          <DraggableDialogTitle>Entity Settings</DraggableDialogTitle>
          <DraggableDialogDescription>
            Configure prepayment and sync settings for this entity.
          </DraggableDialogDescription>
        </DraggableDialogHeader>

        <div className="flex-1 overflow-y-auto p-6">
          <EntitySettingsManagement
            entityId={entityId}
            fetchSettings={entitySettings.handleFetchSettings}
            fetchChartOfAccounts={entitySettings.handleFetchChartOfAccounts}
            saveSettings={(id, settings) => entitySettings.handleSaveSettings(id, settings)}
            onClose={close}
            hideFooter={true}
            onSaveAction={handleEntitySaveAction}
          />
        </div>

        <DraggableDialogFooter className="p-6 border-t flex-shrink-0">
          <Button variant="outline" onClick={close} disabled={isSavingEntity}>
            Cancel
          </Button>
          <Button
            onClick={entitySaveHandler || (() => {})}
            disabled={isSavingEntity || !canSaveEntity || !entitySaveHandler}
          >
            {isSavingEntity && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Save Settings
          </Button>
        </DraggableDialogFooter>
      </DraggableDialogContent>
    </DraggableDialog>
  );
}


